import {
  TableRow,
  TableCell,
  IconButton,
  Grid,
  Typography,
  Button,
  Theme,
  SxProps,
} from '@mui/material';
import { CSSProperties, ReactNode } from 'react';
import { Svgs } from '../../../assets/svg';
import EllipsesMenu from '../../EllipsesMenu';
import ExpandIcon from '../../ExpandIcon';
import Row from '../../Row';
import { PopupMenuItem } from '../../PopupMenu';
import { DataTableHeader, RowData, TableType, LastColumnElement } from '../types';
import { isPrimitive } from '../../../helpers/utilityFunctions';
import { CELL_HORIZONTAL_PADDING, ELLIPSIS_COLUMN_WIDTH, LEADING_COLUMN_WIDTH } from '../DataTable';

export interface BaseTableRowProps {
  headers: DataTableHeader[];
  row: RowData;
  index: number;
  cellVariant?: 'outlined';
  type?: TableType;
  rowStyles?: SxProps<Theme>;
  lastColumnStyles?: CSSProperties;
  lastColumnElement?: LastColumnElement | ((row: any) => LastColumnElement);
  onDeleteClick?: (row: any) => void;
  onButton1Click?: (row: any) => void;
  button1Text?: string;
  button1Disabled?: boolean;
  hasLeadingColumn: boolean;
  flexLayout?: boolean;
  ellipsesItems?: PopupMenuItem[];
  expandedContent?: ReactNode;
  isExpanded?: boolean;
  isNested?: boolean;
  onExpand?: () => void;
  borderOverride?: SxProps<Theme>;
}

export const StandardRow = ({
  headers,
  isExpanded = false,
  isNested = false,
  onExpand = () => {},
  ...props
}: BaseTableRowProps) => {
  const firstContentIndex = headers.findIndex(header => {
    const value = props.row[header.label];
    return value !== null && value !== undefined && value !== '';
  });

  const elementType =
    typeof props.lastColumnElement === 'function'
      ? props.lastColumnElement(props.row)
      : props.lastColumnElement;

  const getCellValue = (value: any) => {
    if (value === undefined || value === null || value === '') {
      return null;
    }
    return isPrimitive(value) ? value : 'Error Reading Table Entry';
  };

  return (
    <TableRow
      sx={{
        ...(props.flexLayout && {
          display: 'flex',
          width: '100%',
        }),
        alignItems: 'center',
      }}>
      {props.hasLeadingColumn && (
        <TableCell
          align='center'
          sx={{
            width: LEADING_COLUMN_WIDTH,
            maxWidth: LEADING_COLUMN_WIDTH,
            minWidth: LEADING_COLUMN_WIDTH,
            flex: 'none',
            padding: props.row.deletableRow && !props.row.isDefault ? 0 : undefined,
          }}>
          {props.row.deletableRow && !props.row.isDefault ? (
            <IconButton color='primary' onClick={() => props.onDeleteClick?.(props.row)}>
              <Svgs.IconDelete />
            </IconButton>
          ) : !isNested && props.expandedContent ? (
            <Grid onClick={onExpand} sx={{ cursor: 'pointer' }}>
              <ExpandIcon isExpanded={isExpanded} onToggle={onExpand} />
            </Grid>
          ) : null}
        </TableCell>
      )}

      {headers.map((header, j) => {
        const rawValue = props.row[header.label];
        const cellValue = getCellValue(rawValue) as ReactNode;
        const isLastCell = j === headers.length - 1;

        const needsExpandIcon = isNested && j === firstContentIndex - 1 && props.expandedContent;
        const isClickable =
          props.expandedContent &&
          ((!isNested && j === 0) || (isNested && j === firstContentIndex));

        if (needsExpandIcon) {
          return (
            <TableCell
              key={j}
              align={header.align}
              variant={props.cellVariant}
              sx={[
                {
                  ...(props.flexLayout &&
                    header.flex !== undefined && {
                      flex: header.flex,
                    }),
                },
                ...(Array.isArray(header.cellSx) ? header.cellSx : [header.cellSx]),
              ]}
              onClick={onExpand}>
              <Grid
                sx={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                }}>
                <Grid
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    width: LEADING_COLUMN_WIDTH,
                    cursor: 'pointer',
                  }}>
                  <ExpandIcon isExpanded={isExpanded} onToggle={onExpand} />
                </Grid>
              </Grid>
              {cellValue}
            </TableCell>
          );
        }

        return (
          <TableCell
            key={j}
            align={header.align}
            variant={props.cellVariant}
            onClick={isClickable ? onExpand : undefined}
            sx={[
              {
                ...(props.flexLayout &&
                  header.flex !== undefined && {
                    flex: header.flex,
                  }),
                ...(isClickable ? { 'cursor': 'pointer', '&:hover': { opacity: 0.7 } } : {}),
                ...(header.align === 'right' &&
                  !isLastCell && {
                    paddingRight: CELL_HORIZONTAL_PADDING,
                  }),
              },
              ...(Array.isArray(header.cellSx) ? header.cellSx : [header.cellSx]),
            ]}>
            {j === 0 ? <Row style={{ alignItems: 'center' }}>{cellValue}</Row> : cellValue}
          </TableCell>
        );
      })}

      {(elementType === 'button' || elementType === 'menu') && (
        <TableCell
          variant='hasButton'
          sx={{
            ...(props.flexLayout &&
              elementType === 'menu' && {
                width: ELLIPSIS_COLUMN_WIDTH,
                maxWidth: ELLIPSIS_COLUMN_WIDTH,
                minWidth: ELLIPSIS_COLUMN_WIDTH,
                flex: 'none',
              }),
            ...(props.flexLayout &&
              elementType === 'button' && {
                width: LEADING_COLUMN_WIDTH,
                maxWidth: LEADING_COLUMN_WIDTH,
                minWidth: LEADING_COLUMN_WIDTH,
                flex: 'none',
              }),
          }}>
          {props.row.isDefault ? (
            <Row style={{ display: 'inline-block', minWidth: '120px', justifySelf: 'flex-end' }}>
              <Row style={{ justifyContent: 'center' }}>
                <Typography variant='p16Bold' color='primary'>
                  Default
                </Typography>
              </Row>
            </Row>
          ) : (
            <Row
              style={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'flex-end',
                ...props.lastColumnStyles,
              }}>
              {elementType === 'button' && (
                <>
                  {props.onButton1Click && props.button1Text && (
                    <Button
                      onClick={() => props.onButton1Click?.(props.row)}
                      disabled={props.button1Disabled}
                      sx={{ typography: { fontSize: 14 }, minWidth: 'fit-content' }}
                      variant='outlined'
                      color='primary'>
                      {props.button1Text}
                    </Button>
                  )}
                </>
              )}
              {elementType === 'menu' && props.ellipsesItems && (
                <EllipsesMenu items={props.ellipsesItems} row={props.row} color='primary' />
              )}
            </Row>
          )}
        </TableCell>
      )}

      {elementType === 'spacer' && (
        <TableCell
          sx={{
            width: LEADING_COLUMN_WIDTH,
            maxWidth: LEADING_COLUMN_WIDTH,
          }}
        />
      )}
    </TableRow>
  );
};

export default StandardRow;
